/* Created by Interactive 63 - https://interactive63.com */
/**
 * Conversion of sampled curves into the point list behind CSS `linear()`. The editor evaluates
 * the same simplified points the exported CSS contains, so previews match the browser.
 */
import { BoundedCache } from './cache';
import { bounceCurve, elasticCurve, springCurve } from './curves';
import type { EasingFunction } from './cubic-bezier';
import { formatNumber } from './format-number';
import type { CurveEasing } from './types';

export type Point = [x: number, y: number];

export interface PointOptions {
  samples?: number;
  tolerance?: number;
  maxPoints?: number;
}

const pointCache = new BoundedCache<Point[]>(100);

/** Perpendicular distance from a point to the line through two others. */
function perpendicularDistance(point: Point, start: Point, end: Point): number {
  const dx = end[0] - start[0];
  const dy = end[1] - start[1];
  const length = Math.hypot(dx, dy);
  if (length === 0) return Math.hypot(point[0] - start[0], point[1] - start[1]);
  return Math.abs(dy * point[0] - dx * point[1] + end[0] * start[1] - end[1] * start[0]) / length;
}

/** Ramer-Douglas-Peucker simplification that always keeps the first and last points. */
export function simplify(points: Point[], tolerance: number): Point[] {
  if (points.length < 3) return points.slice();
  const keep = new Array<boolean>(points.length).fill(false);
  keep[0] = true;
  keep[points.length - 1] = true;
  const stack: [number, number][] = [[0, points.length - 1]];

  for (let range = stack.pop(); range; range = stack.pop()) {
    const [first, last] = range;
    const start = points[first];
    const end = points[last];
    if (!start || !end) continue;
    let farthest = -1;
    let maxDistance = 0;
    for (let i = first + 1; i < last; i += 1) {
      const point = points[i];
      if (!point) continue;
      const distance = perpendicularDistance(point, start, end);
      if (distance > maxDistance) {
        maxDistance = distance;
        farthest = i;
      }
    }
    if (farthest !== -1 && maxDistance > tolerance) {
      keep[farthest] = true;
      stack.push([first, farthest], [farthest, last]);
    }
  }
  return points.filter((_, i) => keep[i]);
}

/**
 * Samples a curve and simplifies it to a short list of points. If the result is longer than
 * `maxPoints`, the tolerance is relaxed until it fits. Coordinates are rounded to 4 decimals.
 */
export function curveToPoints(sampler: EasingFunction, options: PointOptions = {}): Point[] {
  const { samples = 200, maxPoints = 60 } = options;
  let tolerance = options.tolerance ?? 0.002;
  const raw: Point[] = [];
  for (let i = 0; i <= samples; i += 1) {
    const x = i / samples;
    raw.push([x, sampler(x)]);
  }
  let points = simplify(raw, tolerance);
  while (points.length > maxPoints) {
    tolerance *= 1.5;
    points = simplify(raw, tolerance);
  }
  return points.map(([x, y]) => [Number(x.toFixed(4)), Number(y.toFixed(4))]);
}

/** Formats points as a CSS `linear()` function, omitting percentages on exact end points. */
export function pointsToLinearCss(points: readonly Point[]): string {
  const last = points.length - 1;
  const stops = points.map(([x, y], i) => {
    const value = formatNumber(y, 4);
    if ((i === 0 && x === 0) || (i === last && x === 1)) return value;
    return `${value} ${formatNumber(x * 100, 2)}%`;
  });
  return `linear(${stops.join(', ')})`;
}

/** Piecewise-linear interpolation over a point list, clamped at both ends, like CSS `linear()`. */
export function evaluatePoints(points: readonly Point[], t: number): number {
  const first = points[0];
  const last = points[points.length - 1];
  if (!first || !last) return t;
  if (t <= first[0]) return first[1];
  if (t >= last[0]) return last[1];
  for (let i = 1; i < points.length; i += 1) {
    const to = points[i];
    const from = points[i - 1];
    if (!to || !from || t > to[0]) continue;
    const span = to[0] - from[0];
    return span === 0 ? to[1] : from[1] + ((to[1] - from[1]) * (t - from[0])) / span;
  }
  return last[1];
}

/** Builds, and caches, the simplified point list for a spring, bounce or elastic easing. */
export function pointsForEasing(easing: CurveEasing): Point[] {
  return pointCache.get(JSON.stringify(easing), () => {
    switch (easing.type) {
      case 'spring':
        return curveToPoints(springCurve(easing.stiffness, easing.damping, easing.mass));
      case 'bounce':
        return curveToPoints(bounceCurve(easing.bounces, easing.decay));
      case 'elastic':
        return curveToPoints(elasticCurve(easing.oscillations, easing.decay));
    }
  });
}
